import { ChangeDetectionStrategy, Component, forwardRef, inject, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { StorageService } from '@core/services/storage.service';

/**
 * Number input with −/+ buttons (ControlValueAccessor, value: number | null).
 * Accepts comma and dot as decimal separator and shows the user's decimal symbol.
 * The first "+" on an empty field jumps to `seed` (e.g. a typical area) instead of `step`.
 */
@Component({
  selector: 'number-stepper',
  templateUrl: './number-stepper.component.html',
  styleUrls: ['./number-stepper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => NumberStepperComponent), multi: true }
  ]
})
export class NumberStepperComponent implements ControlValueAccessor {
  private readonly storage = inject(StorageService);

  step = input<number>(1);
  min = input<number | null>(0);
  max = input<number | null>(null);
  seed = input<number | null>(null);
  /** Unit shown after the value, text or translation key. */
  suffix = input<string>('');
  placeholder = input<string>('–');
  invalid = input<boolean>(false);

  protected readonly value = signal<number | null>(null);
  protected readonly text = signal('');
  protected readonly disabled = signal(false);

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: number | null): void {
    const number = typeof value === 'number' && !isNaN(value) ? value : null;
    this.value.set(number);
    this.text.set(this.format(number));
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected onInput(text: string): void {
    this.text.set(text);
    const cleaned = text.replace(',', '.').replace(/[^\d.-]/g, '');
    const number = parseFloat(cleaned);
    this.emit(isNaN(number) ? null : number);
  }

  protected onBlur(): void {
    this.text.set(this.format(this.value()));
    this.onTouched();
  }

  protected decrease(): void {
    const value = this.value();
    this.set(value === null ? -this.step() : value - this.step());
  }

  protected increase(): void {
    const value = this.value();
    this.set(value === null ? (this.seed() ?? this.step()) : value + this.step());
  }

  protected canDecrease(): boolean {
    const min = this.min();
    const value = this.value();
    return !this.disabled() && (min === null || value === null || value > min);
  }

  protected canIncrease(): boolean {
    const max = this.max();
    const value = this.value();
    return !this.disabled() && (max === null || value === null || value < max);
  }

  private set(value: number): void {
    const number = this.clamp(this.round(value));
    this.text.set(this.format(number));
    this.emit(number);
    this.onTouched();
  }

  private emit(value: number | null): void {
    this.value.set(value);
    this.onChange(value);
  }

  private clamp(value: number): number {
    const min = this.min();
    const max = this.max();
    if (min !== null && value < min) return min;
    if (max !== null && value > max) return max;
    return value;
  }

  // Avoids floating point noise like 6.000000001 after several 0.5 steps.
  private round(value: number): number {
    const decimals = (this.step().toString().split('.')[1] || '').length;
    const factor = Math.pow(10, Math.max(decimals, 1));
    return Math.round(value * factor) / factor;
  }

  private format(value: number | null): string {
    return value === null ? '' : value.toString().replace('.', this.storage.decimalSymbol || ',');
  }
}
